export const DEFAULT_SYNC_PAGE_SIZE: number = 50;
export const GMAIL_THREAD_PAGE_SIZE: number = 20;
export const GMAIL_MESSAGE_PAGE_SIZE: number = 40;
export const GMAIL_DRAFT_PAGE_SIZE: number = 40;
export const FOLDER_PAGE_SIZE: number = 100;
// Shared IMAP hosts frequently drop the connection on large/slow pulls
// (Nylas surfaces this as a 504 "IMAP provider closed connection"), especially
// with `include_headers` + native date search. Smaller pages = lighter, faster
// requests = far fewer provider-side connection drops during imports.
export const IMAP_THREAD_PAGE_SIZE: number = 25;
export const IMAP_MESSAGE_PAGE_SIZE: number = 25;

export type ProviderListCollection = "drafts" | "messages" | "threads" | "folders";

/**
 * True when the Nylas grant is a Google mail/calendar account.
 * Uses the OAuth `provider` slug from Nylas (typically `google` or `gmail`), not the mailbox
 * domain — Google Workspace addresses (e.g. user@company.de) still use provider `google`.
 */
export function isGmailProvider(provider: string | undefined): boolean {
  return provider !== undefined && /gmail|google/i.test(provider);
}

/** Nylas / Microsoft Graph–style provider strings for calendar email accounts. */
export function isMicrosoftEmailProvider(provider: string | undefined): boolean {
  return (
    provider !== undefined &&
    /microsoft|outlook|office\s*365|office365|graph|ews|exchange/i.test(
      provider,
    )
  );
}

export function isImapLikeProvider(provider: string | undefined): boolean {
  return provider !== undefined && /imap|yahoo|icloud|aol/i.test(provider);
}

/** Short UI label for email account lists: Gmail / M365 / IMAP. */
export function emailAccountProviderShortLabel(
  provider: string | undefined,
): "Gmail" | "M365" | "IMAP" {
  if (isGmailProvider(provider)) return "Gmail";
  if (isMicrosoftEmailProvider(provider)) return "M365";
  return "IMAP";
}

/**
 * On-prem Exchange / EWS grants. Nylas cannot auto-create conferencing (Teams)
 * on EWS, so these must be excluded from the Teams autocreate path.
 */
export function isEwsProvider(provider: string | undefined): boolean {
  return provider !== undefined && /ews/i.test(provider);
}

export function getProviderPageSize(
  provider: string | undefined,
  collection: ProviderListCollection,
): number {
  if (collection === "folders") return FOLDER_PAGE_SIZE;
  if (isGmailProvider(provider)) {
    if (collection === "threads") return GMAIL_THREAD_PAGE_SIZE;
    if (collection === "messages") return GMAIL_MESSAGE_PAGE_SIZE;
    return GMAIL_DRAFT_PAGE_SIZE;
  }
  if (isImapLikeProvider(provider)) {
    if (collection === "threads") return IMAP_THREAD_PAGE_SIZE;
    if (collection === "messages") return IMAP_MESSAGE_PAGE_SIZE;
    return DEFAULT_SYNC_PAGE_SIZE;
  }
  return DEFAULT_SYNC_PAGE_SIZE;
}
